import { PureComponent, type JSX } from "react";

import _, { isEqual } from "lodash";
import { flushSync } from "react-dom";
import styled, { css } from "styled-components";

import Sentry from "@similarweb/sentry-logger";
import { colorsPalettes } from "@similarweb/styles";
import { Pagination, PaginationInput } from "@similarweb/ui-components";

import type { ITableType } from "app/components/React/Table/types";
import { i18nFilter } from "app/filters/i18nFilter";
import { DefaultFetchService } from "app/services/fetchService";
import { allTrackers } from "app/services/track/track";
import { LongLoaderAfterFewSeconds } from "app/UtilitiesAndConstants/UtilitiesComponents/LongLoaderAfterFewSeconds";
import { LoaderListBulletsWrapper } from "pro-features/components/Loaders/src/LoaderListItems";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import { TableNoData } from "./FlexTable/Big/FlexTableStatelessComponents";
import { GhostLoader, SWReactTableWrapperBox } from "./SWReactTableGhostLoader";
import { SWReactTableOptimized } from "./SWReactTableOptimized";

interface ISWReactTableWrapperProps {
    preventAutoCancellation?: boolean;
    getDataCallback?: (data: any) => void;
    recordsField?: string;
    totalRecordsField?: string;
    onDataError?: (...args: any[]) => any;
    noDataTitle?: string;
    noDataSubtitle?: string;
    changePageCallback?: (page: number) => void;
    onPaginationTrack?: (page: number) => void;
    onDataFetch?: (requestParams: FixmeType) => void;
    tableColumns: any[];
    footerComponent?: JSX.Element;
    presentEmptyTable?: boolean;
    initialFilters?: any;
    tableType?: ITableType;
    tableHeight?: number;
    numRowsForLoader?: number;
    tableOptions?: any;
    rowsPerPage?: number;
    pageIndent?: number;
    paginationProps?: {
        captionPosition?: string;
        hasItemsPerPageSelect?: boolean;
        itemsPerPageOptions?: number[];
        hasJumpButton?: boolean;
        itemsPerPageLabel?: string;
        showPagination?: boolean;
    };
    fetchServerPages?: number;
    children?: any;
    onPageChange?: (...args: any[]) => void;
    onSort?: (...args: any[]) => void;
    preventDefaultOnSort?: boolean;
    /** fetch and process data but do not render it. useful when using tables in tabs */
    preventRender?: boolean;
    onFilterChange?: (...args: any[]) => void;
    dataParamsAdapter?: (params: object) => object;
    allowClientSort?: boolean;
    /** transformData will be called in the getData lifecycle, before saving the data to the state. */
    transformData?: (data: any, transformDataOptions?: any) => any;
    transformDataOptions?: any;
    /**  enrichData will be called before the render. It should be used in order to add external data to the rows */
    enrichData?: (data: any) => any;
    requestBody?: unknown;
    /**
     * the table sends tracking of the total results returned from the backend. In the future the field
     * that holds that value could have a different name then the current names. Therefore, we
     * give the option to pass in a name prop to support that field.
     * */
    totalCountProperty?: string;
    preventCountTracking?: boolean;
    /** should we present a separator line between the table and the pagination */
    withFooterLineSeparator?: boolean;
}

export interface ISWReactTableWrapperWithServerDataProps extends ISWReactTableWrapperProps {
    serverApi: string;
    tableData?: {
        records: unknown[];
        totalRecords: number;
    };
    useServerData?: boolean;
}

interface ISWReactTableWrapperWithClientDataProps extends ISWReactTableWrapperProps {
    tableData: any;
    useServerData: boolean;
    serverApi?: undefined;
}

export interface TableTopComponentProps {
    onFilterChange: (params: FixmeType, updateUrlOnChange: boolean) => void;
    onClickToggleColumns: (index: any) => void;
    filtersStateObject: any;
    page: number;
    tableColumns: any[];
    tableOptions: any;
    sort: string;
    asc: boolean;
    isLoadingData: boolean;
}

interface ISWReactTableWrapperState {
    page: number;
    sort: string;
    asc: boolean;
    orderBy: string;
    filters: any;
    totalRecords: number;
    totalPages: number;
    tableRecords: any[];
    pageDataChunks: any[];
    currentChunksStartPage: number;
    tableColumns: any[];
    dataState: number;
    tableMeta?: any;
    tableItemsCount: number | "Error";
    didTrackItemsCount: boolean;
}

export const SWReactTableWrapperFooter = styled.div<{ withFooterLineSeparator?: boolean }>`
    display: flex;
    justify-content: flex-end;
    padding: 10px 15px;
    ${({ withFooterLineSeparator }) =>
        withFooterLineSeparator &&
        css`
            border-top: 1px solid ${colorsPalettes.carbon["100"]};
        `};
    span {
        font-size: 12px !important;
    }
`;

enum dataStates {
    LOADING,
    LOADED,
    ERROR,
}

let manualPageChangeToken = 0;

async function manualPageChage(delay: number) {
    return new Promise((resolve) => {
        clearTimeout(manualPageChangeToken);
        manualPageChangeToken = window.setTimeout(resolve, delay);
    });
}

class SWReactTableWrapper extends PureComponent<
    ISWReactTableWrapperWithServerDataProps | ISWReactTableWrapperWithClientDataProps,
    ISWReactTableWrapperState
> {
    public static defaultProps = {
        preventAutoCancellation: false,
        tableType: "sticky",
        tableHeight: null,
        footerComponent: null,
        useServerData: true,
        presentEmptyTable: false,
        preventRender: false,
        tableData: [],
        tableOptions: {},
        rowsPerPage: 100,
        fetchServerPages: 1,
        transformData: _.identity,
        getDataCallback: () => null,
        changePageCallback: () => null,
        recordsField: "records",
        totalRecordsField: "totalRecords",
        initialFilters: {},
        pageIndent: 0,
        paginationProps: {
            captionPosition: "center",
            hasItemsPerPageSelect: false,
            showPagination: true,
        },
        onDataError: (err: unknown) => Sentry.error("ERROR fetching table data", err),
        enrichData: (data: FixmeType) => data,
        preventCountTracking: false,
        withFooterLineSeparator: false,
    };

    private fetchService;

    private swNavigator;

    private lastGetData: FixmeType;

    constructor(props: ISWReactTableWrapperWithServerDataProps | ISWReactTableWrapperWithClientDataProps) {
        super(props);

        this.fetchService = DefaultFetchService.getInstance();
        this.swNavigator = proNavigator;

        this.state = {
            page: 0,
            sort: "",
            orderBy: "",
            asc: true,
            filters: this.compactFilters(props.initialFilters),
            totalRecords: 0,
            totalPages: 0,
            tableRecords: [],
            pageDataChunks: [],
            currentChunksStartPage: 0,
            tableColumns: props.tableColumns,
            dataState: dataStates.LOADING,
            tableItemsCount: -1,
            didTrackItemsCount: false,
        };
    }

    public componentDidUpdate(
        prevProps: ISWReactTableWrapperWithServerDataProps | ISWReactTableWrapperWithClientDataProps,
    ) {
        if (!isEqual(this.props.requestBody, prevProps.requestBody) && this.props.useServerData) {
            this.getData();
        }
        if (this.props.tableColumns !== prevProps.tableColumns) {
            this.setState({
                tableColumns: this.props.tableColumns,
            });
        }
        if (this.props.preventCountTracking !== prevProps.preventCountTracking) {
            this.trackTableItemsCount();
        }
        if (!this.props.useServerData && !isEqual(this.props.tableData?.records, prevProps.tableData?.records)) {
            const totalRecords = this.props.tableData?.records?.length;
            const pageDataChunks = _.chunk(this.props.tableData?.records, this.props.rowsPerPage);
            this.setState({
                page: 0,
                pageDataChunks,
                totalRecords: totalRecords ?? 0,
                // @ts-ignore FIXME: strict migration - rowsPerPage optional in props but defaultProps provides 100
                totalPages: Math.ceil(totalRecords / this.props.rowsPerPage),
                tableRecords: pageDataChunks[0] ?? [],
                dataState: dataStates.LOADED,
            });
            this.props.getDataCallback?.(this.props.tableData);
        }
    }

    public async componentDidMount() {
        if (this.props.useServerData) {
            await this.getData();
            this.setState({ tableRecords: this.state.pageDataChunks[0] });
        } else {
            const totalRecords = this.props.tableData?.records?.length;
            const pageDataChunks = _.chunk(this.props.tableData?.records, this.props.rowsPerPage);
            this.setState({
                page: 0,
                totalRecords: totalRecords ?? 0,
                // @ts-ignore FIXME: strict migration - rowsPerPage optional in props but defaultProps provides 100
                totalPages: Math.ceil(totalRecords / this.props.rowsPerPage),
                pageDataChunks,
                tableRecords: pageDataChunks?.[0] ?? [],
                dataState: dataStates.LOADED,
            });
            this.props.getDataCallback?.(this.props.tableData);
        }
    }

    public async getData(params = {}, headers?: Record<string, string | number | boolean>) {
        this.setState({ dataState: dataStates.LOADING });
        let allDataParams = { ...this.state.filters };
        if (this.state.sort) {
            allDataParams.sort = this.state.sort;
            allDataParams.asc = this.state.asc;
            allDataParams.orderBy = this.state.orderBy;
        }

        // SIM-24451 - when sorted field in state doesnt exists in tables config - resetting sort to default
        if (this.props.tableColumns.filter((column) => column.field === allDataParams.sort).length === 0) {
            const sortedColumn = this.props.tableColumns.find((column) => column.isSorted);
            if (sortedColumn) {
                allDataParams.sort = sortedColumn.field;
                allDataParams.asc = sortedColumn.sortDirection === "asc";
            }
        }
        allDataParams = { ...allDataParams, ...params };
        // ensure that table state shows sorting field and direction it requests.
        const orderBy = allDataParams.orderBy || allDataParams.orderby;
        const [sortedColumn, sortDirection = "asc"] = allDataParams.sort
            ? [allDataParams.sort, allDataParams.asc ? "asc" : "desc"]
            : orderBy?.split(" ") || [undefined, "asc"];
        this.updateTableColumnsSortState(sortedColumn, sortDirection);
        try {
            const requestParams = this.props.dataParamsAdapter
                ? this.props.dataParamsAdapter(allDataParams)
                : allDataParams;
            this.props.onDataFetch?.(requestParams);
            let promise;
            if (this.props.requestBody) {
                const url = `${this.props.serverApi}?${this.fetchService.requestParams(requestParams)}`;
                promise = this.fetchService.post(url, this.props.requestBody, {
                    headers,
                    preventAutoCancellation: this.props.preventAutoCancellation,
                });
            } else {
                // @ts-ignore FIXME: strict migration - serverApi optional on client-data union but required path here, only reached when useServerData true
                promise = this.fetchService.get(this.props.serverApi, requestParams, {
                    headers,
                    preventAutoCancellation: this.props.preventAutoCancellation,
                });
            }
            this.lastGetData = promise;
            await promise;
            if (promise === this.lastGetData) {
                const data = await this.props.transformData?.(await this.lastGetData, this.props.transformDataOptions);
                const {
                    // @ts-ignore FIXME: strict migration - recordsField optional in props but defaultProps provides "records"
                    [this.props.recordsField]: records, // Data
                    // @ts-ignore FIXME: strict migration - totalRecordsField optional in props but defaultProps provides "totalRecords"
                    [this.props.totalRecordsField]: totalRecords, //TotalCount
                    ...dataMeta
                } = data;
                const pageDataChunks = _.chunk(records, this.props.rowsPerPage);
                // APPSP:1104 & https://github.com/reactwg/react-18/discussions/21
                // This was necessary in order to populate the pageDataChunks state
                //  so that when it is accessed in componentDidMount it has a value.
                //  Otherwise, the tableRecords state will be set in componentDidMount
                //  to undefined and nothing will display in the table.
                flushSync(() => {
                    this.setState({
                        totalRecords,
                        // @ts-ignore FIXME: strict migration - rowsPerPage optional in props but defaultProps provides 100
                        totalPages: Math.ceil(totalRecords / this.props.rowsPerPage),
                        pageDataChunks,
                        tableRecords: pageDataChunks[0],
                        dataState: dataStates.LOADED,

                        tableMeta: { ...dataMeta },
                    });
                });
                const totalResults = this.props.totalCountProperty
                    ? data[this.props.totalCountProperty]
                    : data.totalRecords?.toString() || data.ResultCount?.toString() || data.TotalCount?.toString();

                this.setState({ tableItemsCount: totalResults, didTrackItemsCount: false }, this.trackTableItemsCount);
                this.props.getDataCallback?.(data);
            }
        } catch (err) {
            this.setState({ dataState: dataStates.ERROR });
            this.setState({ tableItemsCount: "Error", didTrackItemsCount: false }, this.trackTableItemsCount);
            this.props.onDataError?.(err);
        }
    }

    private trackTableItemsCount() {
        if (
            !this.props.preventCountTracking &&
            !this.state.didTrackItemsCount &&
            Number(this.state.tableItemsCount) >= 0
        ) {
            this.setState({ didTrackItemsCount: true });
            allTrackers.dispatchTrackEvent("Table", "table-results", `${this.state.tableItemsCount?.toString() || ""}`);
        }
    }

    private onPageChange = async (page: number) => {
        page--;
        if (page > this.state.totalPages - 1) {
            page = this.state.totalPages - 1;
        }
        if (page < 0) {
            page = 0;
        }
        if (page === this.state.page) {
            return;
        }
        const state = page === this.state.page + 1 ? "next" : page === this.state.page - 1 ? "prev" : "manual";
        const value =
            page === this.state.page + 1 || page === this.state.page - 1
                ? this.state.page + 1
                : `${this.state.page + 1}-${page + 1}`;

        if (this.props.onPaginationTrack) {
            this.props.onPaginationTrack(page);
        } else {
            allTrackers.trackEvent("Pagination", "click", `Table/${state}/${value}`);
        }

        this.setState(
            {
                page,
            },
            async () => {
                if (state === "manual") {
                    await manualPageChage(1000);
                }
                if (this.shouldFetchServerPage(page) && this.props.useServerData) {
                    return this.renderServerPage(page);
                } else {
                    return this.renderClientPage(page);
                }
            },
        );
        if (typeof this.props.changePageCallback === "function") {
            this.props.changePageCallback(page);
        }
    };

    private shouldFetchServerPage(page: number) {
        return !(
            page >= this.state.currentChunksStartPage &&
            // @ts-ignore FIXME: strict migration - fetchServerPages optional in props but defaultProps provides 1
            page < this.state.currentChunksStartPage + this.props.fetchServerPages &&
            this.state.pageDataChunks.length > 1
        );
    }

    private async renderServerPage(page: number) {
        // @ts-ignore FIXME: strict migration - pageIndent optional in props but defaultProps provides 0
        const pageNumber = page + this.props.pageIndent;
        // getting new rows from server
        if (this.props.onPageChange) {
            this.props.onPageChange(page);
        } else {
            // @ts-ignore FIXME: strict migration - fetchServerPages optional in props but defaultProps provides 1
            await this.getData({ page: Math.floor(pageNumber / this.props.fetchServerPages) });
        }
        this.setState({
            page,
            // @ts-ignore FIXME: strict migration - fetchServerPages optional in props but defaultProps provides 1
            tableRecords: this.state.pageDataChunks[page % this.props.fetchServerPages],
            // @ts-ignore FIXME: strict migration - fetchServerPages optional in props but defaultProps provides 1
            currentChunksStartPage: Math.floor(page / this.props.fetchServerPages) * this.props.fetchServerPages,
        });
    }

    private renderClientPage(page: number) {
        // getting next chunk
        this.setState({
            page,
            tableRecords: this.state.pageDataChunks[page - this.state.currentChunksStartPage],
        });
    }

    private onSort = async ({ field, sortDirection }: FixmeType) => {
        if (this.props.onSort) {
            this.props.onSort({ field, sortDirection });
            if (!this.props.useServerData || this.props.preventDefaultOnSort) {
                return;
            } else if (
                this.props.allowClientSort &&
                // @ts-ignore FIXME: strict migration - rowsPerPage/fetchServerPages optional in props but defaultProps provides defaults
                this.state.totalRecords <= this.props.rowsPerPage * this.props.fetchServerPages
            ) {
                setTimeout(() => {
                    const allData = this.state.pageDataChunks.reduce((acc, curr) => acc.concat(curr), []);
                    const pos = this.state.asc ? -1 : 1;
                    const sortedData = allData.sort((a: FixmeType, b: FixmeType) =>
                        a[this.state.sort] < b[this.state.sort] ? pos : pos * -1,
                    );
                    if (typeof this.props.getDataCallback === "function") {
                        this.props.getDataCallback({
                            records: sortedData,
                            ...this.state.tableMeta,
                            totalRecords: this.state.totalRecords,
                            dataState: this.state.dataState,
                        });
                    }
                    if (typeof this.props.changePageCallback === "function") {
                        this.props.changePageCallback(0);
                    }
                    const newChunks = _.chunk(sortedData, this.props.rowsPerPage);
                    this.setState({
                        pageDataChunks: newChunks,
                        tableRecords: newChunks[this.state.page],
                    });
                });
            } else {
                await this.getData({
                    sort: field,
                    asc: sortDirection === "asc",
                    orderBy: field + " " + sortDirection,
                });
            }
        } else {
            await this.getData({
                sort: field,
                asc: sortDirection === "asc",
                orderBy: field + " " + sortDirection,
            });
        }
        this.updateTableColumnsSortState(field, sortDirection);
    };

    private updateTableColumnsSortState(field: FixmeType, sortDirection: FixmeType) {
        const tableColumns = this.state.tableColumns.map((column) => {
            if (column.field === field) {
                return {
                    ...column,
                    isSorted: true,
                    sortDirection,
                };
            } else {
                return {
                    ...column,
                    isSorted: false,
                };
            }
        });
        this.setState({
            tableColumns,
            sort: field,
            asc: sortDirection === "asc",
            orderBy: field + " " + sortDirection,
            page: 0,
        });
    }

    private onFilterChange = (params: FixmeType, updateUrlOnChange: boolean) => {
        this.updateFiltersState(params, updateUrlOnChange, () => {
            if (this.props.onFilterChange) {
                return this.props.onFilterChange(params);
            } else {
                return this.getData(this.compactFilters(params));
            }
        });
    };

    private updateFiltersState(params: FixmeType, updateUrlOnChange: boolean, setStateCallback: () => void) {
        if (updateUrlOnChange) {
            this.swNavigator.updateQueryParams(params, { reload: false });
        }
        this.setState(
            {
                filters: this.compactFilters({
                    ...this.state.filters,
                    ...params,
                }),
                page: 0,
            },
            setStateCallback,
        );
    }

    private onClickToggleColumns = (index: FixmeType) => {
        const tableColumns = [...this.state.tableColumns];
        if (Array.isArray(index)) {
            index.forEach((visible, idx) => (tableColumns[idx].visible = visible));
        } else {
            tableColumns[index].visible = !tableColumns[index].visible;
        }
        this.setState({ tableColumns });
    };

    private createTableLoader() {
        //except boolean for having long loader with default props or object for changing itle & subtitle
        return this.props.tableOptions.longLoader ? (
            <LongLoaderAfterFewSeconds
                SecondLoader={LoaderListBulletsWrapper}
                {...this.props.tableOptions.longLoader}
            />
        ) : (
            <GhostLoader rowNum={this.props.numRowsForLoader} />
        );
    }

    private createTableNoData() {
        return (
            <SWReactTableWrapperBox>
                {this.createTopComponent()}
                {this.props.tableOptions.noDataComponent ? (
                    this.props.tableOptions.noDataComponent
                ) : (
                    <TableNoData
                        messageTitle={this.props.tableOptions.noDataTitle || i18nFilter()("global.nodata.notavilable")}
                        messageSubtitle={this.props.tableOptions.noDataSubtitle || ""}
                    />
                )}
            </SWReactTableWrapperBox>
        );
    }

    private createTopComponent(props = {}) {
        return (
            this.props.children &&
            this.props.children({
                onFilterChange: this.onFilterChange,
                onClickToggleColumns: this.onClickToggleColumns,
                filtersStateObject: this.state.filters,
                page: this.state.page,
                tableColumns: this.state.tableColumns,
                tableOptions: this.props.tableOptions,
                sort: this.state.sort,
                asc: this.state.asc,
                orderBy: this.state.orderBy,
                isLoadingData: this.state.dataState === dataStates.LOADING,
                totalCount: this.state.totalRecords,
                ...props,
            })
        );
    }

    private compactFilters(filters: FixmeType) {
        return _.reduce(
            filters,
            (result, value, key) => {
                if (value != null && value !== "") {
                    return {
                        ...result,
                        [key]: value,
                    };
                }
                return result;
            },
            {},
        );
    }

    private hasData() {
        return this.state.tableRecords && this.state.tableRecords.length;
    }

    private showPager() {
        return (
            this.props.paginationProps?.showPagination &&
            (this.state.dataState === dataStates.LOADING || this.hasData())
        );
    }

    public render() {
        const { dataState, tableRecords, totalRecords, page, tableColumns } = this.state;
        const isLoadingTable = dataState === dataStates.LOADING || this.props.tableOptions?.loading;

        if (this.props.preventRender) {
            return null;
        }

        if (dataState === dataStates.ERROR) {
            return this.createTableNoData();
        }
        const enrichedTableRecords = this.props.enrichData?.(tableRecords);
        return (
            <SWReactTableWrapperBox presentEmptyTable={this.props.presentEmptyTable}>
                {this.createTopComponent({ tableRecords: enrichedTableRecords })}
                {isLoadingTable ? (
                    this.createTableLoader()
                ) : (
                    <SWReactTableOptimized
                        type={this.props.tableType}
                        tableHeight={this.props.tableHeight}
                        tableOptions={this.props.tableOptions}
                        tableData={{
                            Records: enrichedTableRecords,
                            TotalCount: totalRecords,
                            page: page + 1,
                            pageSize: this.props.rowsPerPage,
                        }}
                        tableColumns={tableColumns}
                        onSort={this.onSort}
                        presentEmptyTable={this.props.presentEmptyTable}
                    />
                )}
                {this.showPager() ? (
                    <SWReactTableWrapperFooter withFooterLineSeparator={this.props.withFooterLineSeparator}>
                        {dataState === dataStates.LOADED ? (
                            <Pagination
                                page={page + 1}
                                // @ts-ignore FIXME: strict migration - rowsPerPage optional in props but defaultProps provides defaults
                                itemsPerPage={this.props.rowsPerPage}
                                itemsCount={totalRecords}
                                handlePageChange={this.onPageChange}
                                captionElement={(captionProps) => <PaginationInput {...captionProps} />}
                                {...this.props.paginationProps}
                            />
                        ) : null}
                    </SWReactTableWrapperFooter>
                ) : null}
                {this.props.footerComponent}
            </SWReactTableWrapperBox>
        );
    }
}

export default SWReactTableWrapper;
